import { useRef } from "react";

// A notification keeps its role and cohort recipients together, as one list
// of audience ids (audienceTo). The screens show and pick them apart, as
// roles and as cohorts, by finding each id in the role list or the cohort
// list loaded for the pickers. Those lists are only one page, and the
// pickers' searches replace them (and empty them while loading), so an id
// that is not in the list loaded now is not a recipient to drop: the screens
// used to drop it, and the next save sent the shorter list (F-BUG-5, review
// I-1).
//
// The kinds below only grow: an id once seen in the role list stays a role,
// and one seen in the cohort list stays a cohort. An id seen in neither yet
// is kept apart as "other" and still saved.

export const createAudienceKinds = () => ({
  roles: new Set(),
  cohorts: new Set(),
});

// Notes the kind of every role and cohort row given. Returns the kinds.
export const rememberAudienceKinds = (kinds, roleRows = [], cohortRows = []) => {
  (roleRows || []).forEach((row) => row?.id && kinds.roles.add(row.id));
  (cohortRows || []).forEach((row) => row?.id && kinds.cohorts.add(row.id));
  return kinds;
};

// The audience ids sorted into roles, cohorts and the ones whose kind is not
// known yet (other), in their order.
export const splitAudience = (audienceTo, kinds) => {
  const split = { roles: [], cohorts: [], other: [] };
  (audienceTo || []).forEach((id) => {
    if (kinds.roles.has(id)) {
      split.roles.push(id);
    } else if (kinds.cohorts.has(id)) {
      split.cohorts.push(id);
    } else {
      split.other.push(id);
    }
  });
  return split;
};

// The audience ids to save: every role, cohort and other id, once each.
export const joinAudience = (...lists) => [
  ...new Set(lists.flat().filter(Boolean)),
];

// The kinds a screen has seen, kept for as long as it is open. Noting the
// rows during render keeps the kinds up to date for the effects of the same
// render; adding to a set is safe to repeat.
export const useAudienceKinds = (roleRows, cohortRows) => {
  const kinds = useRef(null);
  if (kinds.current === null) {
    kinds.current = createAudienceKinds();
  }
  return rememberAudienceKinds(kinds.current, roleRows, cohortRows);
};
